import { useContext, useState } from "react";
import UserContext from "../context/user-context";
import User from "../passapp-sdk/user";
import Vehicle from "../passapp-sdk/vehicle";
import { VehicleData } from "../shared/EntryMethods/Vehicle/Vehicle";
import {
  userSelectVehicle,
  userShowDialogCompanion,
} from "../store/actions/userAction";
import { fileNames } from "../utils/imageUtil";
import { useStore } from "./useStore";
import useVehicleBrands from "./useVehicleBrands";

const useVehicle = () => {
  const vehicleApi = new Vehicle();
  const user = new User();
  const { authStore } = useStore();
  const { state, dispatch } = useContext(UserContext);
  const { rawBrands } = useVehicleBrands();
  const [loading, setLoading] = useState(false);

  // `findVehicles` arma su etiqueta desde brandId. Mandar sólo el nombre legacy deja el id de la
  // marca anterior intacto: select/update muestran el texto nuevo, pero la búsqueda vuelve a
  // mostrar la marca vieja. Es el mismo contrato que ya usa el flujo del parque logístico.
  const resolveBrand = async (brand = "") => {
    const normalizedBrand = brand.trim().toLocaleLowerCase();
    const match = rawBrands.find(
      (candidate) => candidate.name.trim().toLocaleLowerCase() === normalizedBrand
    );
    if (match?.id || !brand) return { brand, brandId: match?.id };

    // El hook del catálogo se carga al abrir el formulario. Si el guardia guarda antes de que
    // llegue, consultamos puntualmente la marca para no caer otra vez al update legacy sin id.
    try {
      const response = await vehicleApi.getVehicleBrands(authStore.token, brand);
      const candidates = (Array.isArray(response?.data)
        ? response.data
        : Object.values(response?.data || {})) as Array<{ id?: string; name?: string }>;
      const remoteMatch = candidates.find(
        (candidate) => candidate.name?.trim().toLocaleLowerCase() === normalizedBrand
      );
      return { brand, brandId: remoteMatch?.id };
    } catch (error) {
      console.warn("Error resolving vehicle brand:", error);
      return { brand };
    }
  };

  const addVehicleImage = (idVehicle: string, file: any) => {
    return user.uploadImgVehicle(
      authStore.token,
      file,
      idVehicle,
      "insurancePhoto",
      fileNames.INSURANCE_FILENAME
    );
  };

  const addNewVehicle = async (newVehicle: VehicleData) => {
    setLoading(true);
    try {
      const brand = await resolveBrand(newVehicle.brand);
      const response: any = await vehicleApi.addVehicle({
        token: authStore.token,
        user: state.id,
        brand: brand.brand,
        brandId: brand.brandId,
        model: newVehicle.model,
        color: newVehicle.color,
        plate: newVehicle.plate,
        insuranceExpirationDate: newVehicle.insuranceExpirationDate,
      });
      if (response?.status !== "ok" || !response?.data?.id) {
        throw new Error(response?.msg || "No se pudo crear el vehículo.");
      }
      if (newVehicle.insurancePhotoNew) {
        // El alta ya quedó persistida. Una falla aislada de la foto no debe hacer que el guardia
        // reintente crear la misma patente (el buscador la verá como coincidencia exacta).
        try {
          await addVehicleImage(response.data.id, newVehicle.insurancePhotoNew);
        } catch (error) {
          console.warn("Error uploading vehicle insurance photo:", error);
        }
      }
      dispatch(userSelectVehicle(response.data));
      dispatch(userShowDialogCompanion(true));
      return response.data;
    } finally {
      setLoading(false);
    }
  };

  /** El flujo residencial antes sólo dejaba los cambios de un vehículo existente en memoria para
   * que addEvent los procesara como override. Ahora se guardan con el endpoint autoritativo antes
   * de continuar, igual que el flujo del parque logístico. */
  const updateExistingVehicle = async (newVehicle: VehicleData) => {
    setLoading(true);
    try {
      if (!newVehicle.id) throw new Error("Vehículo inválido.");
      const brand = await resolveBrand(newVehicle.brand);
      const response: any = await vehicleApi.updateVehicle({
        token: authStore.token,
        id: newVehicle.id,
        brand: brand.brand,
        brandId: brand.brandId,
        model: newVehicle.model,
        color: newVehicle.color,
        insuranceExpirationDate: newVehicle.insuranceExpirationDate,
      });
      if (newVehicle.insurancePhotoNew) {
        try {
          await addVehicleImage(newVehicle.id, newVehicle.insurancePhotoNew);
        } catch (error) {
          console.warn("Error uploading vehicle insurance photo:", error);
        }
      }
      const updatedVehicle = { ...newVehicle, ...(response?.data || {}) };
      dispatch(userSelectVehicle(updatedVehicle));
      dispatch(userShowDialogCompanion(true));
      return updatedVehicle;
    } finally {
      setLoading(false);
    }
  };
  return { addVehicleImage, addNewVehicle, updateExistingVehicle, loading };
};

export default useVehicle;
